import { Input, message } from "antd";
import { SearchOutlined, DeleteOutlined, DownloadOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import "./AssetView.scss";
import { useEffect, useState } from "react";
import AssetFile from "./AssetFile";
import axios from "axios";

const AssetView = ({ projects, onView, onDelete }) => {
  const [listTabs, setListTabs] = useState([]);
  const [searchStr, setSearchStr] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    let tabs = projects.tabs.map((tab) => ({ ...tab, files: [] }));
    projects.moodboards.forEach((moodboard) => {
      tabs = tabs.map((tab) => {
        if (tab._id === moodboard.tabId ) {
          if (searchStr) {
            if (moodboard.title.toLowerCase().includes(searchStr.toLowerCase())) {
              tab.files.push(moodboard);
            }
          } else {
            tab.files.push(moodboard);
          }
        }
        return tab;
      });
    });
    setListTabs(tabs);
  }, [projects, searchStr]);

  const handleDrop = async (selectedFile) => {
    const reOrderTabIndex = listTabs.findIndex(tab => tab._id === selectedFile.tabId)
    const tabNeedReorder = listTabs[reOrderTabIndex].files
        const sortedArray = [];
        tabNeedReorder.forEach((item, index) =>
          sortedArray.push({ _id: item._id, order: index + 1 })
        );
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/moodboards/re-order`,
            {
              order: sortedArray,
            }
          );
        } catch (e) {
          console.log("order error", e);
        }
  };

  const handleDrag = (fromIndex, toIndex, selectFile) => {
    let tempOrder = [...listTabs];
    const reOrderTabIndex = listTabs.findIndex(tab => tab._id === selectFile.tabId)
    const [movedCard] = tempOrder[reOrderTabIndex].files.splice(fromIndex, 1);
    tempOrder[reOrderTabIndex].files.splice(toIndex, 0, movedCard);
    setListTabs(tempOrder);
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const allFiles = listTabs.flatMap((tab) => tab.files);

  const selectAll = () => {
    setSelectedIds(allFiles.map((f) => f._id));
  };

  const handleDeleteSelected = async () => {
    for (const id of selectedIds) {
      await onDelete(id);
    }
    setSelectedIds([]);
  };

  const getFileName = (file) => {
    if (file.title) return file.title;
    if (file.fileName) return file.fileName;
    if (file.source) return `${file.source.charAt(0).toUpperCase() + file.source.slice(1)} video`;
    if (file.fileUrl) {
      const urlName = file.fileUrl.split("/").pop().split("?")[0];
      return urlName || "download";
    }
    return "download";
  };

  const [downloading, setDownloading] = useState(false);

  const externalSites = ["youtube", "youtu.be", "vimeo", "twitter", "x.com", "instagram", "tiktok", "linkedin"];
  const isExternal = (file) => externalSites.some((s) => file.fileUrl?.includes(s));

  const pollAndDownload = async (id, name) => {
    let errorCount = 0;
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/video/download-status/${id}`);
        errorCount = 0;
        if (res.data.status === "ready") {
          window.open(`${process.env.REACT_APP_API_URL}/video/download-file/${id}?filename=${encodeURIComponent(name)}`, "_blank");
          return true;
        }
        if (res.data.status === "failed") return false;
      } catch {
        errorCount++;
        if (errorCount > 3) return false;
      }
    }
    return false;
  };

  const handleDownloadSelected = async () => {
    const selected = allFiles.filter((f) => selectedIds.includes(f._id) && f.fileUrl);
    if (selected.length === 0) return;

    setDownloading(true);
    message.loading({ content: `Preparing ${selected.length} file${selected.length > 1 ? "s" : ""} for download...`, key: "download", duration: 0 });

    let completed = 0;
    let failed = 0;

    for (const file of selected) {
      const name = getFileName(file);

      if (isExternal(file)) {
        try {
          console.log("Preparing download:", file.fileUrl);
          const res = await axios.post(`${process.env.REACT_APP_API_URL}/video/prepare-download`, {
            url: file.fileUrl,
            filename: name,
          });
          console.log("Prepare response:", res.data);
          const success = await pollAndDownload(res.data.id, name);
          console.log("Download result:", success);
          if (success) completed++;
          else failed++;
        } catch (err) {
          console.error("Download error:", err);
          failed++;
        }
      } else {
        const downloadUrl = `${process.env.REACT_APP_API_URL}/video/download?url=${encodeURIComponent(file.fileUrl)}&filename=${encodeURIComponent(name)}`;
        window.open(downloadUrl, "_blank");
        completed++;
      }

      message.loading({
        content: `Downloading... ${completed + failed} / ${selected.length}`,
        key: "download",
        duration: 0,
      });
    }

    if (failed > 0) {
      message.error({ content: `Downloaded ${completed} file${completed !== 1 ? "s" : ""}. ${failed} failed.`, key: "download", duration: 4 });
    } else {
      message.success({ content: `Downloaded ${completed} file${completed !== 1 ? "s" : ""}.`, key: "download", duration: 3 });
    }
    setDownloading(false);
  };

  return (
    <div className="asset-view-container">
      {selectedIds.length > 0 && (
        <div className="asset-action-bar">
          <div className="asset-action-bar-left">
            <CloseOutlined
              onClick={clearSelection}
              style={{ cursor: "pointer", fontSize: "14px" }}
            />
            <span className="asset-action-bar-count">
              {selectedIds.length} selected
            </span>
            <span
              className="asset-action-bar-select-all"
              onClick={selectedIds.length === allFiles.length ? clearSelection : selectAll}
            >
              {selectedIds.length === allFiles.length ? "Deselect all" : "Select all"}
            </span>
          </div>
          <div className="asset-action-bar-right">
            {/* TODO: Re-enable download when yt-dlp flow is fixed
            {downloading ? (
              <LoadingOutlined style={{ fontSize: "18px" }} />
            ) : (
              <DownloadOutlined
                onClick={handleDownloadSelected}
                style={{ cursor: "pointer", fontSize: "18px" }}
                title={`Download ${selectedIds.length} file${selectedIds.length > 1 ? "s" : ""}`}
              />
            )}
            */}
            <DeleteOutlined
              onClick={handleDeleteSelected}
              style={{ cursor: "pointer", fontSize: "18px", color: "#ff4d4f" }}
              title="Delete selected"
            />
          </div>
        </div>
      )}

      <div className="asset-view-header">
        <div className="asset-row">
          <div>All files</div>
          <div className="total-file-wrapper">{projects.moodboards.length}</div>
        </div>
        <Input
          className="search-file"
          placeholder="Search file"
          prefix={<SearchOutlined />}
          onChange={({target: {value}}) => setSearchStr(value)}
        />
      </div>

      <div className="table-column table-column-header">
        <div></div>
        <div>NAME</div>
        <div>SIZE</div>
        <div>ADDED</div>
        <div>SOURCE</div>
      </div>

      {listTabs.length > 0 &&
        listTabs.map((tab) => (
          <div key={tab._id}>
            <div className="table-column asset-tab-title">
              <div></div>
              <div>{tab.title}</div>
            </div>
            {tab.files.map((file, index) => (
              <AssetFile
                key={file._id}
                index={index}
                file={file}
                handleDrag={handleDrag}
                handleDrop={handleDrop}
                onView={onView}
                onDelete={onDelete}
                selected={selectedIds.includes(file._id)}
                onToggleSelect={() => toggleSelect(file._id)}
              />
            ))}
          </div>
        ))}
    </div>
  );
};

export default AssetView;
